
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  generateReferralCode, 
  getUserReferralCodeByEmail
} from '@/utils/referral/referralCodeService';
import { 
  sendReferralActivationEmail, 
  generateActivationToken, 
  checkBusinessReferralLimit
} from '@/utils/referralService';
import { ReferralUser } from '@/types/userRole';
import { useReferralBankIntegration } from '@/utils/referral/referralBankIntegration';
import { EnhancedSignupData } from '@/types/userSignup';

export const useReferralSignup = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  const { allocateReferralId } = useReferralBankIntegration();
  
  const processReferralSignup = async (
    formData: EnhancedSignupData, 
    businessId: string = 'default-business'
  ): Promise<{ success: boolean, user?: ReferralUser, message?: string, activationToken?: string }> => {
    setIsProcessing(true);
    
    try {
      // Check if fraud was detected
      if (formData.fraudCheck?.isFraud) {
        return {
          success: false,
          message: `Signup blocked: ${formData.fraudCheck.reasons.join(', ')}`
        };
      }
      
      // Check if the business has reached its referral limit
      const { limitReached, currentCount, maxCount, pendingAllowed } = await checkBusinessReferralLimit(businessId);
      
      // Generate a unique user ID
      const userId = `user-${Date.now()}`;
      
      // Allocate a permanent referral ID from the super admin pool
      // This referral ID will be permanent and non-reusable
      let referralCode;
      try {
        // Try to allocate from the referral bank
        const referralId = await allocateReferralId(userId);
        referralCode = referralId.code;
      } catch (error) {
        // Fallback to generating a code if allocation fails
        referralCode = await getUserReferralCodeByEmail(formData.email, userId);
      }
      
      if (!referralCode) {
        throw new Error("Failed to generate referral code");
      }
      
      // Generate an activation token
      const activationToken = generateActivationToken();
      
      // Set initial status based on plan limits
      const activationStatus = limitReached && pendingAllowed ? 'pending' : 'pending';
      
      // Create a new referral user
      const referralUser: ReferralUser = {
        id: userId,
        name: formData.name,
        email: formData.email,
        role: 'user',
        referralCode: referralCode,
        activationStatus: activationStatus,
        activationToken: activationToken,
        createdAt: new Date().toISOString(),
        businessId: businessId,
        // Store security and fraud prevention data
        securityInfo: {
          ipAddress: formData.geoData.ip,
          location: {
            city: formData.geoData.city,
            country: formData.geoData.country,
            latitude: formData.geoData.latitude,
            longitude: formData.geoData.longitude
          },
          machineId: formData.geoData.machineId,
          signupTime: formData.submissionTime
        }
      };
      
      // If limit reached and pending is allowed, show a different message
      if (limitReached && pendingAllowed) {
        toast({
          title: "Referral Limit Notice",
          description: `New referral created in pending status. (${currentCount}/${maxCount})`,
        });
      } else if (limitReached) {
        toast({
          title: "Referral Limit Reached",
          description: `The business has reached its maximum referral limit (${maxCount}).`,
          variant: "destructive",
        });
        
        return { 
          success: false, 
          message: `Referral limit reached (${currentCount}/${maxCount})`
        };
      }
      
      // In a real app, you would save the user to the database here
      console.log('Created referral user:', referralUser);
      
      // Store referral in the local storage for testing/demo purposes
      const referrals = JSON.parse(localStorage.getItem(`business_referrals_${businessId}`) || '[]');
      referrals.push(referralUser);
      localStorage.setItem(`business_referrals_${businessId}`, JSON.stringify(referrals));
      
      // Update referral count in localStorage
      localStorage.setItem(`referralCount_${businessId}`, (currentCount + 1).toString());
      
      // Send the activation email if not in pending
      let emailSent = true;
      if (activationStatus !== 'pending' || !limitReached) {
        emailSent = await sendReferralActivationEmail(
          formData.email,
          formData.name,
          activationToken,
          referralCode
        );
      }
      
      if (!emailSent) {
        toast({
          title: "Email Not Sent",
          description: "Account created but activation email could not be sent. Please contact support.",
          variant: "destructive",
        });
      }
      
      let resultMessage = "";
      if (limitReached && pendingAllowed) {
        resultMessage = "Account created in pending status due to plan limits. It will be activated when the business upgrades their plan.";
      } else {
        resultMessage = emailSent 
          ? "Account created successfully. Please check your email to activate your account." 
          : "Account created but activation email could not be sent.";
      }
      
      return { 
        success: true, 
        user: referralUser,
        activationToken,
        message: resultMessage
      };
    } catch (error) {
      console.error('Error processing referral signup:', error);
      
      toast({
        title: "Signup Error",
        description: error instanceof Error ? error.message : "An unknown error occurred during signup.",
        variant: "destructive",
      });
      
      return { 
        success: false, 
        message: error instanceof Error ? error.message : "An unknown error occurred."
      };
    } finally {
      setIsProcessing(false);
    }
  };
  
  return {
    processReferralSignup,
    isProcessing
  };
};

export default useReferralSignup;
